/**
 * Credit Detail Component
 * 
 * Shows full details of a credit including application history,
 * remaining balance, and void action.
 */

import React, { useState } from 'react';
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Statistic,
  Row,
  Col,
  Timeline,
  Modal,
  Input,
  message,
  Alert,
  Divider,
  Empty,
  Spin,
  Progress,
  Popconfirm,
} from 'antd';
import {
  DollarOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { creditsApi } from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Color constants
const colors = {
  success: '#169959',
  error: '#C32928',
  warning: '#E6B339',
  info: '#179DB5',
  textSecondary: '#8C8C8C',
};

// Credit status config
const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
  APPROVED: { color: colors.success, icon: <CheckCircleOutlined />, label: 'Available' },
  PARTIAL: { color: colors.warning, icon: <ClockCircleOutlined />, label: 'Partially Applied' },
  EXHAUSTED: { color: colors.info, icon: <CheckCircleOutlined />, label: 'Fully Applied' },
  VOIDED: { color: colors.error, icon: <CloseCircleOutlined />, label: 'Voided' },
};

// Credit type labels
const creditTypeLabels: Record<string, string> = {
  CLAIM: 'Claim',
  RETURN: 'Return',
  OVERPAYMENT: 'Overpayment',
  PRICING_ADJUSTMENT: 'Pricing Adjustment',
  OTHER: 'Other',
};

// Party type labels
const partyTypeLabels: Record<string, string> = {
  BUYER: 'Buyer',
  SUPPLIER: 'Supplier',
};

interface CreditApplication {
  id: string;
  amountApplied: number;
  remainingAfter: number;
  appliedAt: string;
  appliedBy?: string;
  allocation?: {
    id: string;
    shipment?: {
      id: string;
      reference: string;
    };
    bankTransaction?: {
      id: string;
      reference: string;
      date: string;
      amount: number;
    };
  };
}

interface CreditDetailData {
  id: string;
  partyType: string;
  partyId?: string;
  partyName: string;
  creditType: string;
  originalAmount: number;
  remainingAmount: number;
  currencyCode: string;
  status: string;
  description?: string;
  referenceNumber?: string;
  xeroCreditNoteId?: string;
  createdAt: string;
  updatedAt: string;
  originShipment?: {
    id: string;
    reference: string;
    contract?: {
      reference: string;
      buyer?: { name: string; shortName: string };
      supplier?: { name: string; shortName: string };
    };
  };
  applications?: CreditApplication[];
}

interface CreditDetailProps {
  /** Credit ID to display */
  creditId: string;
  /** Called when user clicks back */
  onBack?: () => void;
  /** Called when credit is updated (voided, etc.) */
  onUpdate?: () => void;
}

const CreditDetail: React.FC<CreditDetailProps> = ({ creditId, onBack, onUpdate }) => {
  const [voidModalVisible, setVoidModalVisible] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const queryClient = useQueryClient();

  // Fetch credit details
  const {
    data: credit,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['credit', creditId],
    queryFn: async () => {
      const response = await creditsApi.get(creditId);
      return response as unknown as CreditDetailData;
    },
  });

  // Void credit mutation
  const voidCreditMutation = useMutation({
    mutationFn: (reason: string) => creditsApi.void(creditId, reason),
    onSuccess: () => {
      message.success('Credit voided successfully');
      queryClient.invalidateQueries({ queryKey: ['credit', creditId] });
      queryClient.invalidateQueries({ queryKey: ['credits'] });
      queryClient.invalidateQueries({ queryKey: ['credits-summary'] });
      setVoidModalVisible(false);
      setVoidReason('');
      onUpdate?.();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to void credit');
    },
  });

  // Remove application mutation
  const removeApplicationMutation = useMutation({
    mutationFn: (applicationId: string) => creditsApi.removeApplication(applicationId),
    onSuccess: () => {
      message.success('Application removed');
      refetch();
      queryClient.invalidateQueries({ queryKey: ['credits'] });
      queryClient.invalidateQueries({ queryKey: ['credits-summary'] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to remove application');
    },
  });

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!credit) {
    return (
      <Empty description="Credit not found">
        <Button onClick={onBack}>Go Back</Button>
      </Empty>
    );
  }

  const statusInfo = statusConfig[credit.status] ?? { color: colors.success, icon: <CheckCircleOutlined />, label: 'Available' };
  const appliedAmount = Number(credit.originalAmount) - Number(credit.remainingAmount);
  const percentApplied = (appliedAmount / Number(credit.originalAmount)) * 100;
  const canVoid = credit.status !== 'VOIDED' && (!credit.applications || credit.applications.length === 0);

  // Application history columns
  const applicationColumns: ColumnsType<CreditApplication> = [
    {
      title: 'Date',
      dataIndex: 'appliedAt',
      key: 'appliedAt',
      width: 150,
      render: (date) => dayjs(date).format('MMM D, YYYY h:mm A'),
    },
    {
      title: 'Amount Applied',
      dataIndex: 'amountApplied',
      key: 'amountApplied',
      width: 120,
      align: 'right',
      render: (amount) => (
        <Text strong style={{ color: colors.info }}>
          ${Number(amount).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Remaining After',
      dataIndex: 'remainingAfter',
      key: 'remainingAfter',
      width: 120,
      align: 'right',
      render: (amount) => `$${Number(amount).toFixed(2)}`,
    },
    {
      title: 'Applied To',
      key: 'appliedTo',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.allocation?.shipment && (
            <Text>
              <LinkOutlined style={{ marginRight: 4 }} />
              Shipment: {record.allocation.shipment.reference}
            </Text>
          )}
          {record.allocation?.bankTransaction && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Transaction: {record.allocation.bankTransaction.reference || record.allocation.bankTransaction.id.slice(-8)}
              {' '}(${Number(record.allocation.bankTransaction.amount).toFixed(2)})
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Applied By',
      dataIndex: 'appliedBy',
      key: 'appliedBy',
      width: 120,
      render: (by) => by || '-',
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        credit.status !== 'VOIDED' && (
          <Popconfirm
            title="Remove this application?"
            description="The credit amount will be restored."
            onConfirm={() => removeApplicationMutation.mutate(record.id)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={removeApplicationMutation.isPending}
            />
          </Popconfirm>
        )
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Back to List
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            Credit: {credit.referenceNumber || credit.id.slice(-8).toUpperCase()}
          </Title>
          <Tag color={statusInfo.color} icon={statusInfo.icon}>
            {statusInfo.label}
          </Tag>
        </Space>
        {canVoid && (
          <Button
            danger
            icon={<CloseCircleOutlined />}
            onClick={() => setVoidModalVisible(true)}
          >
            Void Credit
          </Button>
        )}
      </div>

      {/* Voided Alert */}
      {credit.status === 'VOIDED' && (
        <Alert
          message="This credit has been voided"
          description={credit.description?.includes('[VOIDED:') 
            ? credit.description.split('[VOIDED:')[1]?.replace(']', '') 
            : 'Credit was voided and cannot be applied.'}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Original Amount"
              value={Number(credit.originalAmount)}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Applied"
              value={appliedAmount}
              precision={2}
              prefix="$"
              valueStyle={{ color: appliedAmount > 0 ? colors.info : undefined }}
            />
            <Progress
              percent={percentApplied}
              size="small"
              showInfo={false}
              strokeColor={colors.info}
              style={{ marginTop: 8 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Remaining"
              value={Number(credit.remainingAmount)}
              precision={2}
              prefix="$"
              valueStyle={{
                color: credit.status === 'EXHAUSTED' ? colors.textSecondary :
                       credit.status === 'VOIDED' ? colors.error : colors.success,
              }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Applications"
              value={credit.applications?.length || 0}
              suffix="transactions"
            />
          </Card>
        </Col>
      </Row>

      {/* Credit Details */}
      <Card title="Credit Details" style={{ marginBottom: 24 }}>
        <Descriptions column={2}>
          <Descriptions.Item label="Credit Type">
            <Tag>{creditTypeLabels[credit.creditType] || credit.creditType}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Party Type">
            <Tag color={credit.partyType === 'BUYER' ? 'blue' : 'green'}>
              {partyTypeLabels[credit.partyType]}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Party Name">{credit.partyName}</Descriptions.Item>
          <Descriptions.Item label="Currency">{credit.currencyCode}</Descriptions.Item>
          <Descriptions.Item label="Origin Shipment">
            {credit.originShipment ? (
              <Space direction="vertical" size={0}>
                <Text>{credit.originShipment.reference}</Text>
                {credit.originShipment.contract && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Contract: {credit.originShipment.contract.reference}
                  </Text>
                )}
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Reference Number">
            {credit.referenceNumber || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {dayjs(credit.createdAt).format('MMM D, YYYY h:mm A')}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {dayjs(credit.updatedAt).format('MMM D, YYYY h:mm A')}
          </Descriptions.Item>
          {credit.xeroCreditNoteId && (
            <Descriptions.Item label="Xero Credit Note">
              <Text copyable>{credit.xeroCreditNoteId}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
        
        {credit.description && (
          <>
            <Divider />
            <div>
              <Text strong>Description</Text>
              <Paragraph style={{ marginTop: 8 }}>
                {credit.description.split('[VOIDED:')[0]}
              </Paragraph>
            </div>
          </>
        )}
      </Card>

      {/* Application History */}
      <Card title="Application History">
        {credit.applications && credit.applications.length > 0 ? (
          <Table
            dataSource={credit.applications}
            columns={applicationColumns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        ) : (
          <Empty description="No applications yet">
            <Text type="secondary">
              This credit hasn't been applied to any transactions yet.
            </Text>
          </Empty>
        )}
      </Card>

      {/* Void Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: colors.error }} />
            Void Credit
          </Space>
        }
        open={voidModalVisible}
        onCancel={() => {
          setVoidModalVisible(false);
          setVoidReason('');
        }}
        onOk={() => voidCreditMutation.mutate(voidReason)}
        confirmLoading={voidCreditMutation.isPending}
        okText="Void Credit"
        okButtonProps={{ danger: true }}
      >
        <Alert
          message="This action cannot be undone"
          description="Voided credits cannot be applied to transactions. Make sure you want to proceed."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div>
          <Text strong>Reason for voiding (optional)</Text>
          <TextArea
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            placeholder="Enter reason..."
            rows={3}
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CreditDetail;
